import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useLearn } from "@/contexts/LearnContext";
import { BookOpen, Hash } from "lucide-react";

export default function SearchCommand({ open, onOpenChange }) {
  const { content } = useLearn();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  // Keyboard shortcut (Cmd/Ctrl+K)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpenChange]);

  const items = useMemo(() => {
    if (!content?.modules) return [];
    const out = [];
    content.modules.forEach((m) => {
      out.push({
        type: "module",
        id: m.id,
        title: `Module ${m.number}: ${m.title}`,
        subtitle: `${m.duration_hours} hrs — ${m.summary}`,
        route: `/module/${m.id}`,
        keywords: `${m.title} ${m.summary} ${m.takeaway}`,
      });
      m.subtopics.forEach((s) => {
        const text = s.sections.map((sec) => `${sec.heading} ${sec.body}`).join(" ");
        out.push({
          type: "subtopic",
          id: `${m.id}-${s.id}`,
          title: s.title,
          subtitle: `in Module ${m.number}: ${m.title}`,
          route: `/module/${m.id}#${s.id}`,
          keywords: `${s.title} ${text}`,
        });
      });
    });
    return out;
  }, [content]);

  const handleSelect = (route) => {
    onOpenChange(false);
    setQuery("");
    navigate(route);
    if (route.includes("#")) {
      setTimeout(() => {
        const [, hash] = route.split("#");
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const modules = items.filter((i) => i.type === "module");
  const subtopics = items.filter((i) => i.type === "subtopic");

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search modules, topics, concepts…"
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Modules">
          {modules.map((it) => (
            <CommandItem
              key={it.id}
              value={`${it.title} ${it.keywords}`}
              onSelect={() => handleSelect(it.route)}
              data-testid={`search-result-${it.id}`}
            >
              <BookOpen />
              <div className="flex flex-col">
                <span className="font-medium">{it.title}</span>
                <span className="text-xs text-muted-foreground line-clamp-1">{it.subtitle}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Sub-topics">
          {subtopics.map((it) => (
            <CommandItem
              key={it.id}
              value={`${it.title} ${it.keywords}`}
              onSelect={() => handleSelect(it.route)}
              data-testid={`search-result-${it.id}`}
            >
              <Hash />
              <div className="flex flex-col">
                <span className="font-medium">{it.title}</span>
                <span className="text-xs text-muted-foreground">{it.subtitle}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
